import { Search, Filter, Download, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

export type UserFilterType = "all" | "pro" | "admin" | "free";

interface UserFiltersProps {
  search: string;
  onSearchChange: (value: string) => void;
  filter: UserFilterType;
  onFilterChange: (value: UserFilterType) => void;
  onExport: () => void;
  totalResults?: number;
}

const filterOptions: { value: UserFilterType; label: string }[] = [
  { value: "all", label: "All Users" },
  { value: "pro", label: "Pro" },
  { value: "admin", label: "Admin" },
  { value: "free", label: "Free" },
];

export function UserFilters({
  search,
  onSearchChange,
  filter,
  onFilterChange,
  onExport,
  totalResults,
}: UserFiltersProps) {
  const hasActiveFilters = search.length > 0 || filter !== "all";

  const clearFilters = () => {
    onSearchChange("");
    onFilterChange("all");
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Search Input */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search users by name or email..."
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-9 pr-9 bg-background"
          />
          <AnimatePresence>
            {search.length > 0 ? (
              <motion.button
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                onClick={() => onSearchChange("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="h-4 w-4" />
              </motion.button>
            ) : null}
          </AnimatePresence>
        </div>

        {/* Filter Dropdown */}
        <Select value={filter} onValueChange={(v) => onFilterChange(v as UserFilterType)}>
          <SelectTrigger className="w-full sm:w-[160px] bg-background">
            <Filter className="h-4 w-4 mr-2 text-muted-foreground" />
            <SelectValue placeholder="Filter" />
          </SelectTrigger>
          <SelectContent>
            {filterOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Export Button */}
        <Button
          variant="outline"
          onClick={onExport}
          className="gap-2"
        >
          <Download className="h-4 w-4" />
          <span className="hidden sm:inline">Export</span>
        </Button>
      </div>

      {/* Active Filters & Results Count */}
      <AnimatePresence>
        {hasActiveFilters || totalResults !== undefined ? (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="flex items-center gap-2 flex-wrap"
          >
            {totalResults !== undefined ? (
              <span className="text-sm text-muted-foreground">
                {totalResults.toLocaleString()} {totalResults === 1 ? "user" : "users"} found
              </span>
            ) : null}

            {hasActiveFilters ? (
              <>
                <span className="text-muted-foreground">|</span>
                {search.length > 0 ? (
                  <Badge variant="secondary" className="gap-1 font-normal">
                    Search: "{search}"
                    <button
                      onClick={() => onSearchChange("")}
                      className="ml-1 hover:text-foreground"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ) : null}
                {filter !== "all" ? (
                  <Badge variant="secondary" className="gap-1 font-normal capitalize">
                    {filter}
                    <button
                      onClick={() => onFilterChange("all")}
                      className="ml-1 hover:text-foreground"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ) : null}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className={cn(
                    "h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
                  )}
                >
                  Clear all
                </Button>
              </>
            ) : null}
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
